import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Color, EmailTemplateColor, Gradient } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getEmailTemplateColors,
  updateEmailTemplateColors,
} from '../../../../core/services/email-templates'
import { getSettingsColors } from '../../../../core/services/festival-settings'
import { ColorCircle } from '../../ColorCircle'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card } from '@/components/ui'
import SelectField from '@/components/select-field'

interface IColorValues {
  bgColorName: string
  headerColorName: string
  textColorName: string
  textHighlightColorName: string
  heroCardBgColorName: string
  heroCardTextColorName: string
  heroCardErrorBgColorName: string
  heroCardErrorTextColorName: string
}

export const ColorSettings: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })
  const [emailColors, setEmailColors] = useState<EmailTemplateColor>()

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [gradients, setGradients] = useState<Gradient[]>()
  const [colors, setColors] = useState<Color[]>()

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getEmailTemplateColors()
        setEmailColors(response.data)
        const response2 = await getSettingsColors()
        const main = find(response2.data, data => data.name === 'PRIMARY')
        const secondary = find(response2.data, data => data.name === 'SECONDARY')
        if (secondary?.gradients && main?.gradients) {
          setGradients([...main?.gradients, ...secondary?.gradients])
        }
        if (main?.colors) {
          setColors(main.colors)
        }
        setTimeout(() => {
          setColorValue('bgColorName', response.data.bgColorName)
          setColorValue('headerColorName', response.data.headerColorName)
          setColorValue('textColorName', response.data.textColorName)
          setColorValue('textHighlightColorName', response.data.textHighlightColorName)
          setColorValue('heroCardBgColorName', response.data.heroCardBgColorName)
          setColorValue('heroCardTextColorName', response.data.heroCardTextColorName)
          setColorValue('heroCardErrorBgColorName', response.data.heroCardErrorBgColorName)
          setColorValue('heroCardErrorTextColorName', response.data.heroCardErrorTextColorName)
        }, 100)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const colorSchema = yup.object().shape({
    bgColorName: yup.string().required('Field required!'),
    headerColorName: yup.string().required('Field required!'),
    textColorName: yup.string().required('Field required!'),
    textHighlightColorName: yup.string().required('Field required!'),
    heroCardBgColorName: yup.string().required('Field required!'),
    heroCardTextColorName: yup.string().required('Field required!'),
    heroCardErrorBgColorName: yup.string().required('Field required!'),
    heroCardErrorTextColorName: yup.string().required('Field required!'),
  })

  const {
    handleSubmit: handleSubmitColor,
    register: registerColor,
    formState: { errors: errorsColor },
    setValue: setColorValue,
    watch: colorWatch,
  } = useForm<IColorValues>({
    mode: 'onChange',
    resolver: yupResolver<IColorValues>(colorSchema),
  })

  const submitColor = async (values: IColorValues) => {
    const data: EmailTemplateColor = {
      bgColorName: values.bgColorName,
      headerColorName: values.headerColorName,
      textColorName: values.textColorName,
      textHighlightColorName: values.textHighlightColorName,
      heroCardBgColorName: values.heroCardBgColorName,
      heroCardTextColorName: values.heroCardTextColorName,
      heroCardErrorBgColorName: values.heroCardErrorBgColorName,
      heroCardErrorTextColorName: values.heroCardErrorTextColorName,
    }
    try {
      const { status } = await updateEmailTemplateColors(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const bgColorNameWatch = colorWatch('bgColorName')
  const headerColorNameWatch = colorWatch('headerColorName')
  const textColorNameWatch = colorWatch('textColorName')
  const textHighlightColorNameWatch = colorWatch('textHighlightColorName')
  const heroCardBgColorNameWatch = colorWatch('heroCardBgColorName')
  const heroCardTextColorNameWatch = colorWatch('heroCardTextColorName')
  const heroCardErrorBgColorNameWatch = colorWatch('heroCardErrorBgColorName')
  const heroCardErrorTextColorNameWatch = colorWatch(
    'heroCardErrorTextColorName',
  )

  const handleCancelColor = async () => {
    if (emailColors) {
      setColorValue('bgColorName', emailColors.bgColorName)
      setColorValue('headerColorName', emailColors.headerColorName)
      setColorValue('textColorName', emailColors.textColorName)
      setColorValue(
        'textHighlightColorName',
        emailColors.textHighlightColorName,
      )
      setColorValue('heroCardBgColorName', emailColors.heroCardBgColorName)
      setColorValue('heroCardTextColorName', emailColors.heroCardTextColorName)
      setColorValue(
        'heroCardErrorBgColorName',
        emailColors.heroCardErrorBgColorName,
      )
      setColorValue(
        'heroCardErrorTextColorName',
        emailColors.heroCardErrorTextColorName,
      )
    }
  }

  return (
    <>
      <div>
        <h4 className="text-title-s text-accent-1 mb-4">Color Settings</h4>

        <Card className="p-4">
          <form
            id="settings-email-color-form"
            onSubmit={handleSubmitColor(submitColor)}
          >
            <div className="flex flex-col items-start gap-3">
              <FormGroup
                label="Header Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === headerColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === headerColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) => gradient.name === headerColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={headerColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('headerColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.headerColorName?.message}
                    name="headerColorName"
                    required
                  />
                  {/* <Form.Select
                      isInvalid={!!errorsColor.headerColorName?.message}
                      {...registerColor('headerColorName')}
                      defaultValue=""
                      disabled={!hasEditPermission}
                    >
                      <option value="" disabled>
                        Select a Color
                      </option>
                      {map(colors, (color) => (
                        <option key={color.id} value={color.name}>
                          {color.name}
                        </option>
                      ))}
                      {map(gradients, (gradient) => (
                        <option key={gradient.id} value={gradient.name}>
                          {gradient.name}
                        </option>
                      ))}
                    </Form.Select> */}
                </div>
              </FormGroup>
              <FormGroup
                label="General Background Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === bgColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === bgColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) => gradient.name === bgColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={bgColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('bgColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.bgColorName?.message}
                    name="bgColorName"
                    required
                  />
                  {/* <Form.Select
                      isInvalid={!!errorsColor.bgColorName?.message}
                      {...registerColor('bgColorName')}
                      defaultValue=""
                      disabled={!hasEditPermission}
                    >
                      <option value="" disabled>
                        Select a Color
                      </option>
                      {map(colors, (color) => (
                        <option key={color.id} value={color.name}>
                          {color.name}
                        </option>
                      ))}
                      {map(gradients, (color) => (
                        <option key={color.id} value={color.name}>
                          {color.name}
                        </option>
                      ))}
                    </Form.Select> */}
                </div>
              </FormGroup>
              <FormGroup
                label="Hero Card General Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === heroCardBgColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === heroCardBgColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) => gradient.name === heroCardBgColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={heroCardBgColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('heroCardBgColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.heroCardBgColorName?.message}
                    name="heroCardBgColorName"
                    required
                  />
                  {/* <Form.Select
                      isInvalid={!!errorsColor.heroCardBgColorName?.message}
                      {...registerColor('heroCardBgColorName')}
                      defaultValue=""
                      disabled={!hasEditPermission}
                    >
                      <option value="" disabled>
                        Select a Color
                      </option>
                      {map(colors, (color) => (
                        <option key={color.id} value={color.name} style={{}}>
                          {color.name}
                        </option>
                      ))}
                      {map(gradients, (color) => (
                        <option key={color.id} value={color.name}>
                          {color.name}
                        </option>
                      ))}
                    </Form.Select> */}
                </div>
              </FormGroup>
              <FormGroup
                label="Hero Card Text Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === heroCardTextColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === heroCardTextColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) =>
                        gradient.name === heroCardTextColorNameWatch,
                    )}
                  />

                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={heroCardTextColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('heroCardTextColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.heroCardTextColorName?.message}
                    name="heroCardTextColorName"
                    required
                  />

                  {/* <Form.Select
                      isInvalid={!!errorsColor.heroCardTextColorName?.message}
                      {...registerColor('heroCardTextColorName')}
                      defaultValue=""
                      disabled={!hasEditPermission}
                    >
                      <option value="" disabled>
                        Select a Color
                      </option>
                      {map(colors, (color) => (
                        <option key={color.id} value={color.name} style={{}}>
                          {color.name}
                        </option>
                      ))}
                      {map(gradients, (color) => (
                        <option key={color.id} value={color.name}>
                          {color.name}
                        </option>
                      ))}
                    </Form.Select> */}
                </div>
              </FormGroup>
              <FormGroup
                label="Hero Card Error Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === heroCardErrorBgColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === heroCardErrorBgColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) =>
                        gradient.name === heroCardErrorBgColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={heroCardErrorBgColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('heroCardErrorBgColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.heroCardErrorBgColorName?.message}
                    name="heroCardErrorBgColorName"
                    required
                  />
                </div>
              </FormGroup>
              <FormGroup
                label="Hero Card Error Text Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === heroCardErrorTextColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === heroCardErrorTextColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) =>
                        gradient.name === heroCardErrorTextColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={heroCardErrorTextColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('heroCardErrorTextColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.heroCardErrorTextColorName?.message}
                    name="heroCardErrorTextColorName"
                    required
                  />
                </div>
              </FormGroup>
              <FormGroup
                label="Accent/Strong/Link Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === textHighlightColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === textHighlightColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) =>
                        gradient.name === textHighlightColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={textHighlightColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('textHighlightColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.textHighlightColorName?.message}
                    name="textHighlightColorName"
                    required
                  />
                </div>
              </FormGroup>
              <FormGroup
                label="Text Color"
                direction="row"
                labelClassName="whitespace-nowrap min-w-[230px]"
              >
                <div className="flex justify-center items-center gap-2">
                  <ColorCircle
                    color1={find(
                      colors,
                      (color) => color.name === textColorNameWatch,
                    )}
                    color2={find(
                      colors,
                      (color) => color.name === textColorNameWatch,
                    )}
                    gradient={find(
                      gradients,
                      (gradient) => gradient.name === textColorNameWatch,
                    )}
                  />
                  <SelectField
                    outline
                    placeholder="Select a Color"
                    value={textColorNameWatch}
                    onValueChange={(value) =>
                      setColorValue('textColorName', value)
                    }
                    options={[
                      ...map(colors, (color) => ({
                        label: color.name,
                        value: color.name,
                      })),
                      ...map(gradients, (gradient) => ({
                        label: gradient.name,
                        value: gradient.name,
                      })),
                    ]}
                    disabled={!hasEditPermission}
                    error={errorsColor.textColorName?.message}
                    name="textColorName"
                    required
                  />
                </div>
              </FormGroup>
            </div>
          </form>
        </Card>

        <div className="flex justify-end w-full gap-2 border-t border-monochrome-120 p-4 mt-4">
          <Button
            onClick={handleCancelColor}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-email-color-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
